import type { HelpArticleDef } from "./articleKit";
import {
  AppLink,
  ArticleLink,
  DeploymentNote,
  P,
  Sect,
  Steps,
  Tip,
  Ui,
} from "./articleKit";

export const MARKETING_ARTICLES: HelpArticleDef[] = [
  {
    slug: "forms",
    title: "Forms: capture leads from any site",
    category: "Marketing & automation",
    minutes: 6,
    summary:
      "Build a form, embed it anywhere with one snippet, and read the fills: who submitted, from which page, and where the data goes.",
    keywords: ["lead capture", "embed", "landing page", "submissions", "captcha", "fills"],
    related: ["workflows-and-lead-scoring", "lists-and-segments", "reports-and-attribution"],
    body: (
      <>
        <P>
          A form is the front door of the whole system. Someone fills it on
          your site, Obvious creates or updates their contact, stamps the
          fill on their timeline with attribution, and any automation you've
          pointed at forms fires. The{" "}
          <AppLink to="/marketing">Forms area</AppLink> is where you build
          them and read the results.
        </P>
        <Sect title="Reading the list">
          <P>
            The forms list shows every form with its total fills, unique
            contacts, activity in the last 30 days, and the most recent fill.
            Click a form and the <Ui>Performance</Ui> tab breaks it down:
            every submission with the contact, date, landing page, and
            first-touch channel; a fills-per-contact rollup that flags repeat
            fillers (usually your warmest leads); and a "Where submissions
            go" card that spells out the plumbing for that specific form.
          </P>
        </Sect>
        <Sect title="Building a form">
          <Steps
            items={[
              <>
                In <AppLink to="/marketing">Forms</AppLink>, click{" "}
                <Ui>New form</Ui>, then open the <Ui>Builder &amp; embed</Ui>{" "}
                tab.
              </>,
              <>
                Add fields from the picker: core contact fields, a message
                box, hidden fields for source tagging, page breaks for
                multi-step forms, and any custom fields you've defined. Drag
                to reorder; mark required ones.
              </>,
              <>
                Set the button label and what happens after submit: a success
                message, or a redirect to your thank-you page.
              </>,
              <>
                Style it: button colors, corner radius, and font (matching
                the host site's font is the default).
              </>,
              <>
                Copy the embed code and paste it into any page. The snippet
                is self-contained; nothing else to install.
              </>,
            ]}
          />
        </Sect>
        <Sect title="Spam protection">
          <P>
            Every form ships with an invisible honeypot that silently drops
            bot submissions. For higher-pressure pages you can also require a
            CAPTCHA (Cloudflare Turnstile, free and invisible for most
            visitors) with one checkbox in the builder.
          </P>
        </Sect>
        <Sect title="Where the data goes">
          <P>
            Fills match contacts by email: new email, new contact; known
            email, the existing record is updated and the fill stacks on
            their timeline. Fills carry the page URL and the visitor's
            first-touch channel, which is what makes the{" "}
            <ArticleLink slug="reports-and-attribution">
              attribution reports
            </ArticleLink>{" "}
            possible. A workflow triggered on <Ui>Form is submitted</Ui> can
            score, tag, route, or enroll the new lead automatically - see{" "}
            <ArticleLink slug="workflows-and-lead-scoring">
              Workflows and lead scoring
            </ArticleLink>
            .
          </P>
        </Sect>
        <DeploymentNote>
          In a preview workspace the fill numbers are simulated sample data,
          and the embed's endpoint goes live with deployment. The builder,
          the embed code shape, and everything you design are the real thing.
        </DeploymentNote>
      </>
    ),
  },
  {
    slug: "lists-and-segments",
    title: "Lists and segments",
    category: "Marketing & automation",
    minutes: 4,
    summary:
      "Dynamic audiences built from filters: who's in them, how they stay current, and how campaigns and sequences use them.",
    keywords: ["audience", "segment", "filters", "dynamic list", "targeting"],
    related: ["email-templates-and-campaigns", "sequences", "contacts-and-companies"],
    body: (
      <>
        <P>
          A list is a saved audience: contacts matching a set of filter
          conditions. Lists are dynamic, so as contacts change (a tag added,
          a score crossed, a campaign opened) they flow in and out on their
          own. Build them at{" "}
          <AppLink to="/marketing/lists">Marketing → Lists</AppLink>.
        </P>
        <Sect title="Building one">
          <P>
            Stack conditions and choose whether a contact must match{" "}
            <Ui>all</Ui> of them or <Ui>any</Ui>. The filter catalog is the
            same one the report builder uses: lifecycle stage, lead score and
            grade, tags, owner, touch channels, newsletter status, and even
            campaign engagement, so "opened the August newsletter but didn't
            click" is a two-condition list.
          </P>
        </Sect>
        <Sect title="Where lists get used">
          <P>
            Lists are the audiences everywhere:{" "}
            <ArticleLink slug="email-templates-and-campaigns">
              campaigns
            </ArticleLink>{" "}
            send to a list,{" "}
            <ArticleLink slug="sequences">sequences</ArticleLink> enroll from
            a list, and the members count updates live as you edit the
            filters, so you always know the audience size before you commit.
          </P>
          <Tip>
            Name lists for the audience, not the campaign: "Warm
            manufacturers, no open deal" outlives any one send and gets
            reused for the next three.
          </Tip>
        </Sect>
      </>
    ),
  },
  {
    slug: "email-templates-and-campaigns",
    title: "Email templates and campaigns",
    category: "Marketing & automation",
    minutes: 5,
    summary:
      "Design branded emails once, send them to a list, and read the results: delivery, opens, clicks, and exactly who engaged.",
    keywords: [
      "bulk email", "newsletter", "send", "open rate", "click rate",
      "template", "can-spam", "unsubscribe", "spam", "footer", "compliance",
    ],
    related: ["lists-and-segments", "sequences", "whats-live-vs-deployment"],
    body: (
      <>
        <P>
          Campaigns are one-off sends: a designed email to a chosen list,
          with per-recipient analytics after. The two pieces live side by
          side under Marketing:{" "}
          <AppLink to="/marketing/emails">Emails</AppLink> is where templates
          are designed, <AppLink to="/marketing/campaigns">Campaigns</AppLink>{" "}
          is where they're sent and measured.
        </P>
        <Sect title="Designing a template">
          <P>
            The template builder is block-based: headings, text, buttons,
            images, bullets, dividers, and your logo, with an accent color
            and email-safe font for the whole design. Templates are reused by
            both campaigns and{" "}
            <ArticleLink slug="sequences">sequences</ArticleLink>, so a good
            starter set pays for itself quickly.
          </P>
        </Sect>
        <Sect title="Sending a campaign">
          <Steps
            items={[
              <>
                Set your physical mailing address on the Emails tab once.
                It's required in every marketing footer (CAN-SPAM), and
                campaigns won't send without it.
              </>,
              <>
                In <AppLink to="/marketing/campaigns">Campaigns</AppLink>,
                create a campaign, pick the template and the list. The
                recipient count shows before you send.
              </>,
              <>
                Send. Delivery, opens (tracking pixel), clicks (wrapped
                links), and unsubscribes record per recipient.
              </>,
            ]}
          />
        </Sect>
        <Sect title="Reading the results">
          <P>
            The campaigns list shows every send with delivery, open rate,
            click rate, and unsubscribes at a glance. Click into one for the
            full breakdown: per-link click performance and a "who engaged"
            table naming every opener and clicker, each linked to their
            contact record - which is where your sales follow-up starts.
            Campaign engagement is also a{" "}
            <ArticleLink slug="lists-and-segments">list filter</ArticleLink>,
            so "clicked but never replied" becomes tomorrow's audience.
          </P>
        </Sect>
        <Sect title="Unsubscribes and the compliance footer">
          <P>
            Every template ends with a locked compliance footer carrying your
            mailing address and an unsubscribe link. It can't be removed, by
            design - it's what keeps your sends legal and your domain
            reputation clean. Unsubscribes are counted per campaign, and the
            send engine honors them: once someone opts out, list-based sends
            stop including them.
          </P>
        </Sect>
        <DeploymentNote>
          Live sending (with per-workspace SPF/DKIM so your domain stays
          healthy) and unsubscribe enforcement run in the send engine at
          deployment. Preview workspaces simulate the send and label the
          numbers.
        </DeploymentNote>
      </>
    ),
  },
  {
    slug: "sequences",
    title: "Sequences: follow-up on rails",
    category: "Marketing & automation",
    minutes: 5,
    summary:
      "Multi-step cadences of emails, waits, and tasks that run until someone replies, plus the enrollment view that shows who's where.",
    keywords: ["cadence", "outreach", "drip", "follow up", "enrollment", "unenroll", "reply"],
    related: ["email-templates-and-campaigns", "lists-and-segments", "tasks-and-calendar"],
    body: (
      <>
        <P>
          A sequence is a cadence: send an email, wait three days, raise a
          call task, wait four more, send the next email. It runs per
          contact until the steps finish or the person replies, and it's how
          one seller keeps a hundred prospects warm without dropping any.
          Build them at{" "}
          <AppLink to="/marketing/sequences">Marketing → Sequences</AppLink>.
        </P>
        <Sect title="Building a cadence">
          <P>
            Steps come in three kinds: <Ui>Email</Ui> (pick a template),{" "}
            <Ui>Wait</Ui> (N days), and <Ui>Task</Ui> (raised for the
            contact's owner, like "Call - reference the last email"). Drag to
            reorder. The flow canvas on the right draws the cadence as you
            build, including the reply branch.
          </P>
        </Sect>
        <Sect title="Enrollment and the reply rule">
          <P>
            Point the sequence at a{" "}
            <ArticleLink slug="lists-and-segments">list</ArticleLink> and its
            members enroll; because lists are dynamic, contacts who join the
            list later enroll too. The one rule that matters: leave{" "}
            <Ui>unenroll on reply</Ui> on. The moment someone writes back,
            automation steps aside and the conversation is yours. Nothing
            reads worse than a robotic follow-up after a human reply.
          </P>
          <P>
            One dependency to know: reply detection needs inbound email to
            reach Obvious, through inbox sync or the BCC address (see{" "}
            <ArticleLink slug="inbox-and-email-capture">
              Inbox and email capture
            </ArticleLink>
            ). Connect your mailbox before taking a cadence live, or replies
            can't stop the machine.
          </P>
        </Sect>
        <Sect title="Reading enrollment">
          <P>
            The sequences list shows each cadence's active, completed, and
            replied counts. Click in and the enrollment table names every
            contact with their status and current step, so "who exactly is
            on step 3?" has a one-click answer.
          </P>
        </Sect>
        <DeploymentNote>
          Cadences execute when outbound sending goes live with deployment.
          The step engine is built and tested; preview workspaces show
          simulated enrollment so the reporting shape is visible now.
        </DeploymentNote>
      </>
    ),
  },
  {
    slug: "workflows-and-lead-scoring",
    title: "Workflows and lead scoring",
    category: "Marketing & automation",
    minutes: 6,
    summary:
      "Trigger → action automations, the 0-100 lead score they feed on, and where to see why any contact scored what they did.",
    keywords: ["automation", "triggers", "actions", "score", "grading", "routing", "points"],
    related: ["forms", "keep-an-eye-on", "sequences"],
    body: (
      <>
        <P>
          Workflows are the busywork layer: watch for a trigger, run some
          actions. Lead scoring is the number system many of them feed on.
          Both live at{" "}
          <AppLink to="/marketing/workflows">Marketing → Workflows</AppLink>.
        </P>
        <Sect title="Workflows: when X, do Y">
          <P>
            Triggers: a contact is created, a form is submitted, a deal
            changes stage, the lead score crosses a threshold, a tag is
            added, or a contact goes quiet for N days. Actions: adjust the
            score, change the owner, add a tag, enroll in a{" "}
            <ArticleLink slug="sequences">sequence</ArticleLink>, create a
            task, or send an email. Chain several actions on one trigger; the
            flow canvas draws the result. The workflows list shows how often
            each one fires, and clicking in shows the recent run history with
            the contacts involved.
          </P>
        </Sect>
        <Sect title="How the lead score works">
          <P>
            Every contact carries a 0 to 100 score built from enabled rules,
            each worth points you control: engages with email, repeat site
            visits, came in through a form or search, active this week,
            marked hot, senior title, on an open deal, and so on. Scores map
            to grades (A at 65+, B at 45+, C at 25+). The rules table sits at
            the bottom of the{" "}
            <AppLink to="/marketing/workflows">Workflows page</AppLink>; flip
            rules off or reweight the points to match your funnel.
          </P>
        </Sect>
        <Sect title="Where the score shows up, and why it says what it says">
          <P>
            The badge appears on contact pages, Hot Contacts, and the{" "}
            <ArticleLink slug="keep-an-eye-on">Keep an eye on</ArticleLink>{" "}
            panel. Hover any badge and it lists the exact signals and points
            behind that number - no black box. If the scores feel off, tune
            the rule weights; every badge recomputes immediately.
          </P>
          <Tip>
            Start simple: leave the default weights for two weeks, then look
            at which A-grade contacts actually turned into deals and adjust
            one or two rules. Small moves beat rebuilds.
          </Tip>
        </Sect>
        <DeploymentNote>
          Workflow definitions and scoring rules are live and saved now;
          automatic execution against real events (and the run log) starts
          with deployment. Preview run counts are labeled sample data.
        </DeploymentNote>
      </>
    ),
  },
];
